import { Link, useNavigate } from 'react-router-dom'
import { Bell, LogOut, ChevronLeft, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/config/routes'
import Badge from '@/components/ui/Badge'

const DRAFT_KEY = 'siglat-description-draft'

function loadDraftText() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return ''
    return JSON.parse(raw)?.text ?? ''
  } catch (_) { return '' }
}

/**
 * Nav — top header bar.
 *
 * @param {string}  title      — page title shown in center (mobile)
 * @param {boolean} showBack   — show back chevron instead of logo
 * @param {func}    onBack     — override back navigation
 * @param {boolean} transparent — transparent background (for hero pages)
 */
export default function Nav({
  title,
  showBack = false,
  onBack,
  transparent = false,
}) {
  const { profile, isAdmin, signOut } = useAuth()
  const navigate = useNavigate()
  const [showLogoutWarn, setShowLogoutWarn] = useState(false)
  const [copied, setCopied]                 = useState(false)

  const handleBack = () => {
    if (onBack) { onBack(); return }
    navigate(-1)
  }

  function handleSignOutClick() {
    const draft = loadDraftText()
    if (draft.trim()) {
      setShowLogoutWarn(true)  // has unsaved draft — warn first
    } else {
      signOut()
    }
  }

  function handleCopy() {
    const draft = loadDraftText()
    navigator.clipboard.writeText(draft).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const logoHref = isAdmin
    ? ROUTES.ADMIN
    : profile?.role === 'worker'
      ? ROUTES.WORKER_HOME
      : ROUTES.BOOK

  return (
    <>
      <header className={`
        sticky top-0 z-40 w-full safe-top
        ${transparent
          ? 'bg-transparent'
          : 'bg-surface-base/95 backdrop-blur-md border-b border-surface-border'}
      `}>
        <div className="page-container flex items-center justify-between h-14 gap-3">

          {/* Left: Back button or Logo */}
          {showBack ? (
            <button
              onClick={handleBack}
              className="flex items-center gap-1.5 text-ink-secondary hover:text-ink-primary transition-colors -ml-1 p-1"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </button>
          ) : (
            <Link to={logoHref} className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 bg-siglat-teal rounded-sm flex items-center justify-center">
                <span className="text-surface-base text-xs font-black tracking-tight">SK</span>
              </div>
              {!title && (
                <span className="font-display font-bold text-sm text-ink-primary hidden sm:block">
                  Siglat
                </span>
              )}
            </Link>
          )}

          {/* Center: Page title (mobile only) */}
          {title && (
            <h1 className="font-semibold text-sm text-ink-primary flex-1 text-center">
              {title}
            </h1>
          )}

          {/* Right: actions */}
          {profile && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Role badge — desktop only */}
              <div className="hidden sm:block mr-1">
                <Badge variant={profile.role} label={profile.role} />
              </div>

              {/* Notifications */}
              <button className="relative p-2 text-ink-muted hover:text-ink-primary transition-colors rounded-md hover:bg-surface-overlay">
                <Bell className="w-5 h-5" />
              </button>

              {/* Sign out */}
              <button
                onClick={handleSignOutClick}
                className="p-2 text-ink-muted hover:text-ink-primary transition-colors rounded-md hover:bg-surface-overlay"
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Logout warning popup (shown only when draft exists) ─────────────── */}
      {showLogoutWarn && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowLogoutWarn(false)} />
          <div className="relative w-full max-w-lg bg-surface-raised rounded-t-2xl px-5 pt-5 pb-8 safe-bottom flex flex-col gap-4">
            <div className="w-10 h-1 bg-surface-border rounded-full mx-auto -mt-1 mb-1" />
            <div>
              <p className="font-display text-base font-bold text-ink-primary mb-1">
                You have an unsaved description
              </p>
              <p className="text-sm text-ink-muted leading-relaxed">
                Your encoded description is saved for up to <span className="text-siglat-teal font-medium">7 days</span> and will be restored when you return. But to be safe — copy and paste it somewhere before signing out.
              </p>
            </div>

            <button
              onClick={handleCopy}
              className="flex items-center justify-center gap-2 w-full border border-surface-border rounded-xl py-3 text-sm font-medium text-ink-secondary hover:border-siglat-teal hover:text-siglat-teal transition-colors"
            >
              {copied
                ? <><Check className="w-4 h-4 text-green-400" /> Copied!</>
                : <><Copy className="w-4 h-4" /> Copy my description</>
              }
            </button>

            <div className="flex gap-2">
              <button
                onClick={() => setShowLogoutWarn(false)}
                className="flex-1 py-3 rounded-xl border border-surface-border text-sm font-medium text-ink-secondary hover:bg-surface-overlay transition-colors"
              >
                Stay
              </button>
              <button
                onClick={() => { setShowLogoutWarn(false); signOut() }}
                className="flex-1 py-3 rounded-xl bg-siglat-red/10 border border-siglat-red/30 text-sm font-medium text-siglat-red hover:bg-siglat-red/20 transition-colors"
              >
                Sign out anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
